/**
 * FloatingKIButton - Verschiebbarer KI-Assistent Button
 * 
 * Features:
 * - Draggable (per Maus/Touch verschiebbar)
 * - Floating (schwebt über allen Inhalten)
 * - 3 farbige Benachrichtigungen
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import ZeoIcon from './ZeoIcon';

export default function FloatingKIButton({ onOpen, notifications }) {
  const clampToViewport = (pos) => {
    const maxX = Math.max(0, window.innerWidth - 80);
    const maxY = Math.max(0, window.innerHeight - 80);
    return {
      x: Math.max(0, Math.min(pos.x, maxX)),
      y: Math.max(0, Math.min(pos.y, maxY)),
    };
  };

  const [position, setPosition] = useState(() => {
    const saved = localStorage.getItem('ki_button_position');
    if (saved) {
      try { return clampToViewport(JSON.parse(saved)); } catch { /* default */ }
    }
    return { x: window.innerWidth - 100, y: window.innerHeight - 100 };
  });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef({ startX: 0, startY: 0, offsetX: 0, offsetY: 0, moved: false });

  useEffect(() => {
    if (!isDragging) localStorage.setItem('ki_button_position', JSON.stringify(position));
  }, [position, isDragging]);

  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const ref = dragRef.current;
    ref.startX = e.clientX;
    ref.startY = e.clientY;
    ref.offsetX = e.clientX - position.x;
    ref.offsetY = e.clientY - position.y;
    ref.moved = false;

    const onMove = (mv) => {
      if (!ref.moved && (Math.abs(mv.clientX - ref.startX) > 5 || Math.abs(mv.clientY - ref.startY) > 5)) {
        ref.moved = true;
        setIsDragging(true);
      }
      if (ref.moved) {
        setPosition({
          x: Math.max(0, Math.min(window.innerWidth - 60, mv.clientX - ref.offsetX)),
          y: Math.max(0, Math.min(window.innerHeight - 60, mv.clientY - ref.offsetY)),
        });
      }
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      if (!ref.moved) onOpen?.();
      setTimeout(() => setIsDragging(false), 0);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [position, onOpen]);

  const handleTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    const ref = dragRef.current;
    ref.startX = touch.clientX;
    ref.startY = touch.clientY;
    ref.offsetX = touch.clientX - position.x;
    ref.offsetY = touch.clientY - position.y;
    ref.moved = false;

    const onMove = (mv) => {
      const t = mv.touches[0];
      if (!ref.moved && (Math.abs(t.clientX - ref.startX) > 5 || Math.abs(t.clientY - ref.startY) > 5)) {
        ref.moved = true;
        setIsDragging(true);
      }
      if (ref.moved) {
        setPosition({
          x: Math.max(0, Math.min(window.innerWidth - 60, t.clientX - ref.offsetX)),
          y: Math.max(0, Math.min(window.innerHeight - 60, t.clientY - ref.offsetY)),
        });
      }
    };
    const onEnd = () => {
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      if (!ref.moved) onOpen?.();
      setTimeout(() => setIsDragging(false), 0);
    };
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd);
  }, [position, onOpen]);

  useEffect(() => {
    const handleResize = () => {
      setPosition(prev => ({
        x: Math.max(0, Math.min(prev.x, window.innerWidth - 80)),
        y: Math.max(0, Math.min(prev.y, window.innerHeight - 80)),
      }));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset-Position: Doppelklick auf Button setzt ihn zurueck in die rechte untere Ecke
  const handleDoubleClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const reset = { x: window.innerWidth - 100, y: window.innerHeight - 100 };
    setPosition(reset);
    localStorage.setItem('ki_button_position', JSON.stringify(reset));
  }, []);

  const { aufgaben = 0, nachrichten = 0, erledigt = 0 } = notifications || {};
  const totalNotifications = aufgaben + nachrichten + erledigt;

  return (
    <div
      className={`fixed z-[99999] select-none ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        touchAction: 'none',
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onDoubleClick={handleDoubleClick}
      data-testid="floating-ki-button"
    >
      {/* Main Button */}
      <div
        className={`
          w-16 h-16 rounded-full shadow-2xl 
          flex items-center justify-center 
          transition-all duration-200
          ${isDragging ? 'scale-95 opacity-80' : 'hover:scale-110'}
        `}
        style={{ boxShadow: '0 4px 24px rgba(83, 74, 183, 0.5)' }}
        role="button"
        tabIndex={0}
        title="KI-Assistent öffnen (ziehen zum Verschieben)"
        data-testid="floating-ki-button-circle"
      >
        <ZeoIcon size={64} />
      </div>

      {/* Notification Badges */}
      {totalNotifications > 0 && (
        <div className="absolute -top-1 -right-1 flex gap-0.5">
          {aufgaben > 0 && (
            <span
              className="w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse shadow-lg"
              title={`${aufgaben} neue Aufgabe${aufgaben > 1 ? 'n' : ''}`}
              data-testid="badge-aufgaben"
            >
              {aufgaben > 9 ? '9+' : aufgaben}
            </span>
          )}
          {nachrichten > 0 && (
            <span
              className="w-5 h-5 bg-orange-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse shadow-lg"
              title={`${nachrichten} neue Nachricht${nachrichten > 1 ? 'en' : ''}`}
              data-testid="badge-nachrichten"
            >
              {nachrichten > 9 ? '9+' : nachrichten}
            </span>
          )}
          {erledigt > 0 && (
            <span
              className="w-5 h-5 bg-green-500 text-white text-xs font-bold rounded-full flex items-center justify-center shadow-lg"
              title={`${erledigt} erledigt${erledigt > 1 ? 'e Aufgaben' : 'e Aufgabe'}`}
              data-testid="badge-erledigt"
            >
              {erledigt > 9 ? '9+' : erledigt}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
